import { startTransition, useCallback, useRef, useState } from 'react';
import { apiFetch } from '../apiClient';
import { JiraService } from '../../services/jira';
import { ProfileService, type ProfileData } from '../../services/profileService';
import { LS_ORG_ID, LS_PRODUCT_ID } from '../config/storageKeys';

/**
 * Full update historically required `userProfile` in localStorage only. This merges:
 * - localStorage profile (if present)
 * - GET /api/jira-credentials when email/token are missing (owners; `integrations.configure`)
 * - active product Jira key from GET /api/auth/me (falls back to org default project from API)
 * Then writes back to localStorage so the next run works offline from cache.
 */
async function resolveProfileForFullUpdate(): Promise<ProfileData | null> {
  const local = ProfileService.getProfile();

  let email = (local?.email || '').trim();
  let jiraToken = (local?.jiraToken || '').trim();
  const name = local?.name || '';
  let jiraProject = (local?.jiraProject || '').trim();

  let orgProjectFallback = '';

  try {
    const credRes = await apiFetch('/api/jira-credentials');
    if (credRes.ok) {
      const cfg = (await credRes.json()) as {
        email?: string | null;
        token?: string | null;
        project?: string | null;
      };
      if (!email && cfg.email) email = String(cfg.email).trim();
      if (!jiraToken && cfg.token) jiraToken = String(cfg.token).trim();
      if (cfg.project && String(cfg.project).trim()) {
        orgProjectFallback = String(cfg.project).trim();
      }
    }
  } catch {
    /* network — continue with local only */
  }

  try {
    const meRes = await apiFetch('/api/auth/me');
    if (meRes.ok) {
      const me = (await meRes.json()) as {
        organizations?: Array<{
          id: string;
          products?: Array<{ id: string; jira_project_key?: string | null }>;
        }>;
      };
      const orgId = localStorage.getItem(LS_ORG_ID);
      const productId = localStorage.getItem(LS_PRODUCT_ID);
      const org = me.organizations?.find((o) => o.id === orgId);
      const prod = org?.products?.find((p) => p.id === productId);
      const pk = prod?.jira_project_key;
      if (pk && String(pk).trim()) {
        jiraProject = String(pk).trim();
      }
    }
  } catch {
    /* ignore */
  }

  if (!jiraProject) {
    jiraProject = orgProjectFallback || '';
  }

  if (!email || !jiraToken) {
    return null;
  }

  const merged: ProfileData = {
    name,
    email,
    jiraToken,
    jiraProject,
  };

  ProfileService.saveProfile(merged);
  return merged;
}

async function errorMessageFromResponse(response: Response, fallback: string): Promise<string> {
  try {
    const text = await response.clone().text();
    const j = JSON.parse(text) as { error?: string };
    if (j.error && typeof j.error === 'string') return j.error;
  } catch {
    /* ignore */
  }
  return fallback;
}

function rethrowIfNetworkUnreachable(err: unknown): never {
  const msg = err instanceof Error ? err.message : String(err);
  if (
    err instanceof TypeError &&
    (msg === 'Failed to fetch' || msg.includes('NetworkError') || msg.includes('Load failed'))
  ) {
    throw new Error(
      'Cannot reach the API server. For local development run `npm run server` (default port 3001) alongside the React app, or set REACT_APP_API_URL / proxy so /api routes reach the backend.'
    );
  }
  throw err;
}

export const useDatabaseUpdate = (
  jiraService: JiraService,
  onUpdateComplete?: () => void,
  onProgressCallback?: (progress: number) => void
) => {
  const [isUpdating, setIsUpdating] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressMessage, setProgressMessage] = useState<string | null>(null);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const lastProgressRef = useRef(0);
  const progressCallbackRef = useRef(onProgressCallback);

  // Обновляем ref при изменении callback
  progressCallbackRef.current = onProgressCallback;

  const addLog = useCallback((message: string) => {
    const timestamp = new Date().toLocaleString('en-US');
    startTransition(() => {
      setLogs((prev) => [...prev, `[${timestamp}] ${message}`]);
    });
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
    setError(null);
    setProgress(0);
    setProgressMessage(null);
    lastProgressRef.current = 0;
    // Сбрасываем прогресс в DOM
    if (progressCallbackRef.current) {
      progressCallbackRef.current(0);
    }
  }, []);

  const updateDatabase = useCallback(
    async (options: { startFrom?: number; maxTaskNumber?: number }) => {
      setIsUpdating(true);
      setProgress(0);
      lastProgressRef.current = 0;
      setError(null);
      addLog('Starting database update...');

      // Сбрасываем прогресс в DOM
      if (progressCallbackRef.current) {
        progressCallbackRef.current(0);
      }

      try {
        await jiraService.updateDatabase({
          ...options,
          onProgress: (progressValue) => {
            // Обновляем только если значение изменилось
            if (progressValue !== lastProgressRef.current) {
              lastProgressRef.current = progressValue;

              // Обновляем state
              setProgress(progressValue);

              // Обновляем DOM напрямую через callback
              if (progressCallbackRef.current) {
                progressCallbackRef.current(progressValue);
              }

              // Добавляем лог для каждого изменения прогресса
              addLog(`Progress: ${progressValue}%`);
            }
          },
        });

        addLog('Database update completed successfully!');

        // Гарантируем, что прогресс показывает 100%
        if (progressCallbackRef.current) {
          progressCallbackRef.current(100);
        }
        setProgress(100);

        // Call the callback to refresh data
        if (onUpdateComplete) {
          addLog('Refreshing displayed data...');
          onUpdateComplete();
        }
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        addLog(`Error: ${errorMessage}`);
      } finally {
        setIsUpdating(false);
      }
    },
    [jiraService, addLog, onUpdateComplete]
  );

  const quickUpdate = useCallback(async () => {
    await updateDatabase({
      startFrom: 4717,
      maxTaskNumber: 5717,
    });
  }, [updateDatabase]);

  const fullUpdate = useCallback(async () => {
    setIsUpdating(true);
    setProgress(0);
    setProgressMessage(null);
    lastProgressRef.current = 0;
    setError(null);
    clearLogs();

    try {
      const profile = await resolveProfileForFullUpdate();

      if (!profile) {
        throw new Error(
          'Jira email and API token are required. Configure them in Settings → Project Settings → Organization Jira account and click Save Changes. If you are not an owner/administrator, ask an admin to configure org Jira credentials (GET /api/jira-credentials is restricted).'
        );
      }

      const projectKey = profile.jiraProject?.trim();
      if (!projectKey) {
        throw new Error(
          'Jira project key is required. Set the default org project or product Jira key in Settings → Project Settings, or save your profile with a project key.'
        );
      }

      addLog(`🚀 Starting Full Update for project: ${projectKey}`);
      addLog(`📋 Will load all tasks starting from ${projectKey}-1`);

      console.log('✅ Jira credentials resolved for full update:', {
        hasEmail: !!profile.email,
        hasToken: !!profile.jiraToken,
        project: profile.jiraProject,
        source: 'localStorage merged with server when needed',
      });

      // Clear database first (all worklog rows for current org/product)
      addLog('🗑️ Clearing database...');
      let clearResponse: Response;
      try {
        clearResponse = await apiFetch('/api/worklog?all=true', {
          method: 'DELETE',
        });
      } catch (e) {
        rethrowIfNetworkUnreachable(e);
      }

      if (!clearResponse.ok) {
        throw new Error(await errorMessageFromResponse(clearResponse, 'Failed to clear database'));
      }
      addLog('✅ Database cleared');

      // Phase 1: Find last Jira issue first, show result, then only start load
      addLog('🔍 Phase 1: Finding last Jira issue...');
      setProgressMessage('Finding last Jira issue...');
      let findTaskResponse: Response;
      try {
        findTaskResponse = await apiFetch('/api/find-last-task', {
          method: 'POST',
        });
      } catch (e) {
        rethrowIfNetworkUnreachable(e);
      }

      if (!findTaskResponse.ok) {
        throw new Error(
          await errorMessageFromResponse(findTaskResponse, 'Failed to find last task')
        );
      }

      const reader = findTaskResponse.body?.getReader();
      const decoder = new TextDecoder();
      let lastTaskNumber = 0;

      if (reader) {
        try {
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            const chunk = decoder.decode(value);
            const lines = chunk.split('\n');

            for (const line of lines) {
              if (line.startsWith('data: ')) {
                let data: Record<string, unknown>;
                try {
                  data = JSON.parse(line.slice(6)) as Record<string, unknown>;
                } catch {
                  console.error('SSE JSON parse error, line:', line);
                  continue;
                }

                if (data.type === 'complete') {
                  lastTaskNumber = data.maxTaskNumber as number;
                  const lastIssueText = lastTaskNumber
                    ? `The last Jira issue is ${projectKey}-${lastTaskNumber}.`
                    : String(data.message || 'No issues found');
                  setProgressMessage(lastIssueText);
                  addLog(lastTaskNumber ? `✅ ${lastIssueText}` : `⚠️ ${lastIssueText}`);
                } else if (data.type === 'error') {
                  const errText =
                    typeof data.error === 'string' ? data.error : 'Find last task failed';
                  addLog(`❌ Server error: ${errText}`);
                  throw new Error(errText);
                } else if (data.type === 'success' && data.taskNumber != null) {
                  const tn = data.taskNumber as number;
                  if (tn % 10 === 0 || tn < 20) {
                    setProgressMessage(
                      `Finding last Jira issue... (checked up to ${projectKey}-${tn})`
                    );
                  }
                }
                if (data.message) {
                  const tn = data.taskNumber as number | undefined;
                  if (tn != null && tn % 50 === 0) {
                    addLog(String(data.message));
                  } else if (tn == null || data.type === 'success') {
                    addLog(String(data.message));
                  }
                }
              }
            }
          }
        } catch (streamError) {
          console.error('Stream reading error:', streamError);
          addLog(
            `⚠️ Stream error: ${streamError instanceof Error ? streamError.message : 'Unknown'}`
          );
          throw streamError;
        }
      }

      if (!lastTaskNumber || lastTaskNumber === 0) {
        throw new Error('Failed to determine last task number. Check server logs for details.');
      }

      // Phase 2: Load all tasks — progress bar will show 0–100% here
      addLog(
        `📥 Phase 2: Loading all tasks from ${projectKey}-1 to ${projectKey}-${lastTaskNumber}...`
      );
      addLog(`⏳ Started at: ${new Date().toLocaleTimeString()}`);
      setProgressMessage(
        `The last Jira issue is ${projectKey}-${lastTaskNumber}. Loading into database...`
      );
      setProgress(0);
      if (progressCallbackRef.current) progressCallbackRef.current(0);

      let updateResponse: Response;
      try {
        updateResponse = await apiFetch('/api/update-database', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            startFrom: 1,
            maxTaskNumber: lastTaskNumber,
          }),
        });
      } catch (e) {
        rethrowIfNetworkUnreachable(e);
      }

      if (!updateResponse.ok) {
        throw new Error(
          await errorMessageFromResponse(updateResponse, 'Failed to update database')
        );
      }

      const updateReader = updateResponse.body?.getReader();
      const updateDecoder = new TextDecoder();
      let totalWorklogs = 0;
      let updateBuffer = '';
      let sawUpdateComplete = false;

      if (updateReader) {
        readUpdate: while (true) {
          const { done, value } = await updateReader.read();
          if (done) break;

          updateBuffer += updateDecoder.decode(value, { stream: true });
          const lines = updateBuffer.split('\n');
          updateBuffer = lines.pop() || '';

          for (const line of lines) {
            if (line.trim() === '' || !line.startsWith('data: ')) continue;

            let data: Record<string, unknown>;
            try {
              data = JSON.parse(line.slice(6)) as Record<string, unknown>;
            } catch {
              console.error('SSE JSON parse error (update-database):', line);
              continue;
            }

            if (data.type === 'heartbeat') {
              continue;
            }

            if (data.type === 'complete') {
              totalWorklogs = (data.totalWorklogs as number) ?? 0;
              sawUpdateComplete = true;
              setProgressMessage(null);
              addLog(`✅ Complete! Loaded ${totalWorklogs} worklog entries into database`);
              setProgress(100);
              if (progressCallbackRef.current) {
                progressCallbackRef.current(100);
              }
              break readUpdate;
            }

            if (data.type === 'error') {
              const m = typeof data.message === 'string' ? data.message : 'Database update failed';
              throw new Error(m);
            }

            if (data.progress !== undefined) {
              const p = data.progress as number;
              setProgress(p);
              if (progressCallbackRef.current) {
                progressCallbackRef.current(p);
              }
              if (data.message) {
                setProgressMessage(String(data.message));
                addLog(String(data.message));
              }
            }
          }
        }
      }

      if (!sawUpdateComplete) {
        throw new Error(
          'Connection closed before the database update finished. Check that the API server is running; if the run was long, try again — progress should continue with the latest fixes.'
        );
      }

      addLog(
        `✅ Loaded ${totalWorklogs} worklog entries from ${projectKey}-1 to ${projectKey}-${lastTaskNumber}`
      );
      addLog(`⏰ Finished at: ${new Date().toLocaleTimeString()}`);
      addLog(`📊 Data has been saved to database and will appear in Work Log Data table`);

      if (onUpdateComplete) {
        addLog('🔄 Refreshing Work Log Data table...');
        onUpdateComplete();
        addLog('✨ Work Log Data table updated!');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      setError(errorMessage);
      setProgressMessage(null);
      addLog(`❌ Error: ${errorMessage}`);
    } finally {
      setIsUpdating(false);
    }
  }, [addLog, clearLogs, onUpdateComplete]);

  return {
    isUpdating,
    progress,
    progressMessage,
    logs,
    error,
    updateDatabase,
    quickUpdate,
    fullUpdate,
    addLog,
    clearLogs,
  };
};
